import { useEffect, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Layout,
  NetworkInfo,
  RiskSignalDetails,
  RiskIndicatorLimited,
  RiskIndicatorBlackList,
  Button,
  AddressInfo,
  SanctionNote,
  MisttrackData,
  PdfReportForm,
} from "@/components";
import {
  PAGES,
  ChainsDictionary,
  AmlStatusEnum,
  TxDirectionEnum,
  RiskKeys,
  BlackConnectionEnum,
  AMLBOT_WEB_HREF,
} from "@/config";
import { useAmlHistoryStore, useUserStore } from "@/store";
import { getIconByNetwork } from "@/helpers";
import blacklistFreeIcon from "@/assets/images/filepcheck.svg";

import styles from "./AmlCheckDetails.module.sass";
import { CheckMadeOn } from "@/components/AddressInfo/CheckMadeOn";
import { AddressShorter } from "@/components/AddressShorter/AddressShorter";

const AmlCheckDetails = () => {
  const location = useLocation();
  const itemId = location.state?.itemId;
  const navigate = useNavigate();
  const { fetchAmlItemById, history } = useAmlHistoryStore();
  const theInterval = useRef<any>(null);
  const { refCode } = useUserStore((state) => state.user);

  const currentItem = useMemo(() => {
    if (!itemId) {
      return null;
    }
    return history?.items.find((item) => item.uuid === itemId) ?? null;
  }, [history?.items, itemId]);

  const isPending = currentItem?.status === AmlStatusEnum.PENDING;
  const isSuccess = currentItem?.status === AmlStatusEnum.SUCCESS;

  const performRecheck = useCallback(async () => {
    await fetchAmlItemById(itemId);
  }, [itemId, fetchAmlItemById]);

  useEffect(() => {
    const shouldRecheck = isPending;

    if (shouldRecheck) {
      theInterval.current = setInterval(performRecheck, 15000);
    } else {
      clearInterval(theInterval.current);
      theInterval.current = null;
    }

    return () => clearInterval(theInterval.current);
  }, [currentItem, isPending, performRecheck]);

  const darkMarketNote = useMemo(() => {
    const darkMarketPresent =
      Number(currentItem?.signalTags.includes(RiskKeys.DARK_MARKET) ? 100 : 0) >
      0;

    if (!darkMarketPresent) {
      return "";
    }

    if (currentItem?.tx) {
      return currentItem.direction === TxDirectionEnum.DEPOSIT
        ? "This transaction contains assets from dark market. That's why it can be blocked."
        : "The recipient's address has connections to dark market. This transaction can be blocked.";
    }

    return "This address has connections with dark market. Transactions from\\to such an address have a high risk of being blocked.";
  }, [currentItem]);

  const blackConnectionNote = useMemo(() => {
    if (!currentItem?.blackListsConnections) return "";

    const kind =
      currentItem.itemKindShort ?? (currentItem.tx ? "tx" : "address");
    const conn = currentItem.blackListsConnections;

    switch (conn) {
      case BlackConnectionEnum.OUT_INTERACTION:
        return kind === "tx"
          ? "The recipient's address has connections with blacklists"
          : "The address has connections with blacklists";
      case BlackConnectionEnum.OUT:
        if (currentItem.status === AmlStatusEnum.SUCCESS) {
          return kind === "tx"
            ? "The recipient's address is blacklisted"
            : "The address is blacklisted";
        }
        return "";
      case BlackConnectionEnum.IN:
        return "One of the sender's addresses is blacklisted";
      default:
        return currentItem.tx
          ? "The address has connections with blacklists"
          : "The address is blacklisted";
    }
  }, [currentItem]);

  const hasMistTrackData = useMemo(() => {
    return !!(
      currentItem?.extras &&
      (!!currentItem?.extras?.tags?.length ||
        !!Object.keys(currentItem?.extras?.services).length)
    );
  }, [currentItem?.extras]);

  if (currentItem === null) {
    return null;
  }

  const { network, address } = currentItem;
  const chainInfo = ChainsDictionary[network];
  const networkName = chainInfo?.name || network;

  const handleNavigateSignup = () => {
    const link = `${AMLBOT_WEB_HREF}/ref-signup${refCode ? `?ref=${refCode}` : ""}`;
    window.open(link, "_blank");
  };

  return (
    <Layout
      title="AML Risk Result"
      isShowBackButton
      renderFooter={
        isPending ? null : (
          <Button
            text="Signup to get 1 free check!"
            className={styles.fullDetailsButton}
            onClick={handleNavigateSignup}
          />
        )
      }
      onBack={() => navigate(PAGES.AML_HISTORY)}
    >
      <div className={styles.result}>
        <div className={styles.head}>
          <NetworkInfo icon={getIconByNetwork(network)} title={networkName} />

          {address && <AddressShorter address={address} />}
        </div>

        {!blackConnectionNote && !isPending && (
          <div className={styles.blacklist}>
            <img
              src={blacklistFreeIcon}
              loading="lazy"
              alt="Not Blacklisted"
              className={styles.blacklistIcon}
            />
            <span className={styles.blacklistLabel}>Not Blacklisted</span>
          </div>
        )}

        {!!blackConnectionNote && (
          <RiskIndicatorBlackList
            note={blackConnectionNote}
            className={styles.riskIndicator}
          />
        )}

        {!blackConnectionNote && (
          <RiskIndicatorLimited
            value={currentItem.riskScoreLevel}
            isChecking={isPending}
            className={styles.riskIndicator}
          />
        )}

        {isPending && (
          <div className={styles.pendingNote}>
            Please wait for the results. This usually takes a few minutes, rarely up to a few hours.
          </div>
        )}

        {!!darkMarketNote && (
          <div className={styles.riskDescription}>{darkMarketNote}</div>
        )}

        {isPending && (
          <div className={styles.signalsSkeleton}>
            <div className={styles.skeletonWrap}>
              <div className={styles.skeletonTitle} />
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLine} />
            </div>
            <div className={styles.skeletonWrap}>
              <div className={styles.skeletonTitle} />
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLine} />
            </div>
            <div className={styles.skeletonWrap}>
              <div className={styles.skeletonTitle} />
              <div className={styles.skeletonLine} />
            </div>
          </div>
        )}

        {!!currentItem.signalTags.length && !isPending && (
          <RiskSignalDetails item={currentItem} />
        )}

        {isSuccess && <PdfReportForm itemId={itemId} />}

        {hasMistTrackData && (
          <MisttrackData item={currentItem} isLoading={isPending} />
        )}

        <SanctionNote item={currentItem} isLoading={isPending} />

        <AddressInfo item={currentItem} isLoading={isPending} />

        <CheckMadeOn item={currentItem} isLoading={isPending} />
        
      </div>
    </Layout>
  );
};

export default AmlCheckDetails;
